/**
 * EntityPage
 *
 * Renders the dereferenced view of a data, ontology, or arbitrary RDF entity.
 * When `type` is `data` or `ontology` the URI is reconstructed from the route
 * path; when `type` is `dereference` the URI is read from the `?uri=` query
 * parameter, allowing any URI (e.g. external ones such as `rdfs:label`) to be
 * described via DESCRIBE against the local knowledge graph.
 *
 * Sections:
 *  - Top bar: RDF format buttons (Turtle / RDF/XML / JSON-LD)
 *  - Properties (collapsible)
 *  - Geometry map (collapsible, only if gsp:hasGeometry present)
 *  - See Also (collapsible, only if same-type resources exist)
 */

import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ButtonLink,
  Heading,
  Text,
  Breadcrumbs,
  BreadcrumbsLink,
  BreadcrumbsSpan,
  Link,
  Icon,
} from '@bn-apps/design-system-ui-react';
import { Container } from '@bn-apps/dim-ui-react';
import { ArrowOutward, GridOn, Map, Link as LinkIcon } from '@bn-apps/design-system-icons';
import { useEntity, shortenUri } from '@hooks/useEntity';
import { useGeometry } from '@hooks/useGeometry';
import { useSeeAlso } from '@hooks/useSeeAlso';
import { isInternalUri, pathToUri, uriToDereferencePath } from '@utils/prefix.utils';
import EntityPropertyTable from './EntityPropertyTable';
import EntityMap from './EntityMap';
import EntityRdfViewer from './EntityRdfViewer';
import EntitySection from './EntitySection';
import LoadingState from '@components/common/LoadingState/LoadingState';
import routes from '@config/routes';
import styles from './EntityPage.module.scss';
import appStyles from '@/App.module.scss';

interface EntityPageProps {
  /** Whether this is a data, ontology, or generic dereference entity */
  type: 'data' | 'ontology' | 'dereference';
}

const SHOW_URIS_STORAGE_KEY = 'dim-entity-show-uris';

function readShowUrisPreference(): boolean {
  try {
    return localStorage.getItem(SHOW_URIS_STORAGE_KEY) === 'true';
  } catch {
    // localStorage may be unavailable in private browsing or restricted contexts.
    return false;
  }
}

function isHttpUri(uri: string): boolean {
  try {
    const parsed = new URL(uri);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

const EntityPage = ({ type }: EntityPageProps): JSX.Element => {
  const { t } = useTranslation();
  const { '*': entityPath } = useParams();
  const [searchParams] = useSearchParams();

  // URI view is opt-in. Friendly names are shown by default.
  const [showUris, setShowUris] = useState(readShowUrisPreference);

  useEffect(() => {
    try {
      localStorage.setItem(SHOW_URIS_STORAGE_KEY, String(showUris));
    } catch {
      // Ignore storage failures; the preference still works for this page visit.
    }
  }, [showUris]);

  // Reconstruct full URI: for data/ontology routes from the path,
  // for the generic dereference route from the `?uri=` query parameter.
  const uri =
    type === 'dereference'
      ? decodeURIComponent(searchParams.get('uri') || '')
      : pathToUri(entityPath || '', type);

  // For the dereference route the URI is required; surface a clear error
  // instead of silently spinning on an empty URI.
  const missingUri = type === 'dereference' && !uri;

  const { properties, prefixes, loading, error, label } = useEntity(uri);
  const { wkt, loading: geoLoading } = useGeometry(properties);
  const { resources: seeAlsoResources, typeUri, loading: seeAlsoLoading } = useSeeAlso(
    uri,
    properties
  );

  const displayError = missingUri ? t('entity.missingUriError') : error;
  const displayLoading = !missingUri && loading;
  const hasResolvedEntity = !displayLoading && !displayError;
  const hasProperties = properties.length > 0;

  const shortenedUri = Object.keys(prefixes).length > 0 ? shortenUri(uri, prefixes) : uri;
  const displayLabel = label || shortenedUri;

  const typeLabel =
    type === 'data'
      ? t('entity.dataEntity')
      : type === 'ontology'
        ? t('entity.ontologyEntity')
        : t('entity.dereferenceEntity');

  const showGeometry = !geoLoading && wkt !== null;
  const showSeeAlso = !seeAlsoLoading && seeAlsoResources.length > 0;
  const showOpenOriginalUri =
    type === 'dereference' &&
    !missingUri &&
    hasResolvedEntity &&
    !hasProperties &&
    !isInternalUri(uri) &&
    isHttpUri(uri);
  const emptyStateDescription = showOpenOriginalUri
    ? t('entity.noPropertiesExternalDescription')
    : t('entity.noPropertiesDescription');

  return (
    <Container className={appStyles.pageContent}>
      <div className={styles.entityPage}>
        <Breadcrumbs>
          <BreadcrumbsLink asChild className={styles.breadCrumbsLink}>
            <RouterLink to={routes.home}>{t('common.home')}</RouterLink>
          </BreadcrumbsLink>
          <BreadcrumbsSpan>{typeLabel}</BreadcrumbsSpan>
          <BreadcrumbsSpan currentPage>{displayLabel}</BreadcrumbsSpan>
        </Breadcrumbs>

        <div className="bn-mbe-800">
          <Heading level={1} fontSize="display3xl">
            {displayLabel}
          </Heading>
          <Text fontSize="sm" className={styles.entityUri}>
            <a
              href={uri}
              className={styles.externalLink}
              target="_blank"
              rel="noopener noreferrer"
              title={uri}
            >
              {uri}
            </a>
          </Text>
        </div>

        {hasResolvedEntity && (
          <div className={styles.entityTopBar}>
            <div className={styles.entityTopBarMain}>
              <EntityRdfViewer uri={uri} properties={properties} prefixes={prefixes} />
            </div>
          </div>
        )}

        {displayLoading && (
          <LoadingState />
        )}

        {displayError && (
          <div className={styles.errorContainer}>
            <Text>{t('entity.error')}</Text>
            <Text fontSize="sm">{displayError}</Text>
          </div>
        )}

        {hasResolvedEntity && (
          <div className={styles.sectionsWrapper}>
            {/* Properties */}
            <EntitySection
              title={t('entity.sections.properties')}
              icon={<Icon component={GridOn} size={600} />}
              defaultOpen
            >
              {hasProperties ? (
                <EntityPropertyTable
                  properties={properties}
                  prefixes={prefixes}
                  showUris={showUris}
                  onShowUrisChange={setShowUris}
                />
              ) : (
                <div className={styles.emptyState}>
                  <Text>{t('entity.noProperties')}</Text>
                  <Text fontSize="sm">{emptyStateDescription}</Text>
                  {showOpenOriginalUri && (
                    <div className={styles.emptyStateActions}>
                      <ButtonLink
                        hierarchy="secondary"
                        size="sm"
                        href={uri}
                        target="_blank"
                        rel="noopener noreferrer"
                        iconEnd={ArrowOutward}
                      >
                        {t('entity.openOriginalUri')}
                      </ButtonLink>
                    </div>
                  )}
                </div>
              )}
            </EntitySection>

            {/* Geometry map */}
            {showGeometry && (
              <EntitySection
                title={t('entity.sections.geometry')}
                icon={<Icon component={Map} size={600} />}
                defaultOpen
              >
                <EntityMap wkt={wkt!} />
              </EntitySection>
            )}

            {/* See Also */}
            {showSeeAlso && (
              <EntitySection
                title={t('entity.sections.seeAlso')}
                icon={<Icon component={LinkIcon} size={600} />}
                defaultOpen={false}
              >
                <ul className={styles.seeAlsoList}>
                  {seeAlsoResources.map((resourceUri) => {
                    const shortened = shortenUri(resourceUri, prefixes);
                    const dereferencePath = uriToDereferencePath(resourceUri);
                    return (
                      <li key={resourceUri} className={styles.seeAlsoItem}>
                        <RouterLink
                          to={dereferencePath}
                          className={styles.internalLink}
                          title={resourceUri}
                        >
                          {shortened}
                        </RouterLink>
                        {typeUri && (
                          <span className={styles.seeAlsoType}>
                            {shortenUri(typeUri, prefixes)}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </EntitySection>
            )}
          </div>
        )}

        <div className={styles.helpLink}>
          <Link asChild>
            <RouterLink to={routes.helpDereferencing}>{t('entity.helpLink')}</RouterLink>
          </Link>
        </div>
      </div>
    </Container>
  );
};

export default EntityPage;